import { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import AuthCallback from "./pages/AuthCallback";
import DemoMetrics from "./pages/DemoMetrics";
import ContractorDetail from "./pages/ContractorDetail";
import ContractorForm from "./pages/ContractorForm";
import Contractors from "./pages/Contractors";
import { BrandLogo } from "./components/Brand";
import { ErrorBoundary } from "./components/ErrorBoundary";
import ExecutiveBoard from "./pages/ExecutiveBoard";
import Finance from "./pages/Finance";
import Landing from "./pages/Landing";
import MyDashboard from "./pages/MyDashboard";
import Admin from "./pages/Admin";
import Operations from "./pages/Operations";
import Expenses, { ExpensesAdvances, ExpensesTravel, ExpensesIndex } from "./pages/Expenses";
import Payroll, { PayrollAdjustments, PayrollGratuity, PayrollIndex } from "./pages/Payroll";
import Appraisals, { AppraisalsIndex, AppraisalsScoring } from "./pages/Appraisals";
import Leave, { LeaveAccrual, LeaveCompOff, LeaveIndex } from "./pages/Leave";
import Learning, {
  LearningIndex, LearningMatrix, LearningNeeds, LearningOutcomes, LearningProgrammes, LearningSuccession,
} from "./pages/Learning";
import Lifecycle, {
  LifecycleApprovals, LifecycleAttendance, LifecycleExits, LifecycleGate, LifecycleHiring, LifecycleIndex,
  LifecycleRoster,
  LifecycleMovements, LifecycleOrg, LifecyclePayroll,
} from "./pages/Lifecycle";
import Recruitment, {
  RecruitmentCandidate, RecruitmentChannels, RecruitmentCorrespondence, RecruitmentIndex, RecruitmentNew,
  RecruitmentPaperwork, RecruitmentRequest,
  RecruitmentOpening,
} from "./pages/Recruitment";
import People, {
  PeopleAssignments, PeopleCompliance, PeopleConduct, PeopleFiles, PeopleDirectory, PeopleIndex, PeoplePerson,
  PeopleWorkforce,
} from "./pages/People";
import EmployeeForm from "./pages/people/EmployeeForm";
import MasterForm from "./pages/master/MasterForm";
import NewSupplierFile from "./pages/procure/NewSupplierFile";
import RfqLetter from "./pages/procure/RfqLetter";
import Pos from "./pages/Pos";
import Orders from "./pages/Orders";
import Procure from "./pages/Procure";
import Process from "./pages/Process";
import ProjectDetail from "./pages/ProjectDetail";
import Labour, { LabourIndex, LabourQueue, LabourReconcile } from "./pages/Labour";
import Projects from "./pages/Projects";
import Reports from "./pages/Reports";
import Sourcing from "./pages/Sourcing";
import SignIn from "./pages/SignIn";

// OneFleet add-on (D-15/D-16): the whole /fleet module is one lazy boundary; it declares its own routes
const FleetModule = lazy(() => import("./pages/fleet/index"));
// The warehouse console, the same way: one lazy boundary that declares its own routes from its registry
const WarehouseModule = lazy(() => import("./pages/warehouse/index"));

function Splash() {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#F6F8FA] text-brand-grey">
      <div className="text-center">
        <BrandLogo variant="mark" decorative className="mx-auto h-12 animate-pulse" />
        <p className="mt-3 text-sm">{t("auth.checking")}</p>
      </div>
    </main>
  );
}

/** Gate for every authenticated route: shows the sign-in page until Keycloak has issued a token. */
function RequireAuth() {
  const { status } = useAuth();
  if (status === "loading") return <Splash />;
  if (status === "anonymous") return <SignIn />;
  return <Outlet />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route element={<RequireAuth />}>
            <Route path="/" element={<Landing />} />
            <Route path="/my" element={<MyDashboard />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/executive" element={<ExecutiveBoard />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/labour" element={<Labour />}>
              <Route index element={<LabourIndex />} />
              <Route path="approvals" element={<LabourQueue />} />
              <Route path="reconciliation" element={<LabourReconcile />} />
            </Route>
            <Route path="/projects/sourcing" element={<Sourcing />} />
            <Route path="/projects/sourcing/:name" element={<Sourcing />} />
            <Route path="/projects/:name" element={<ProjectDetail />} />
            <Route path="/operations" element={<Operations />} />
            <Route path="/fleet/*" element={<Suspense fallback={<Splash />}><FleetModule /></Suspense>} />
            <Route path="/warehouse/*" element={<Suspense fallback={<Splash />}><WarehouseModule /></Suspense>} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/people" element={<People />}>
              <Route index element={<PeopleIndex />} />
              <Route path="directory" element={<PeopleDirectory />} />
              <Route path="lifecycle" element={<Lifecycle />}>
                <Route index element={<LifecycleIndex />} />
                <Route path="approvals" element={<LifecycleApprovals />} />
                <Route path="gate" element={<LifecycleGate />} />
                <Route path="attendance" element={<LifecycleAttendance />} />
                <Route path="roster" element={<LifecycleRoster />} />
                <Route path="hiring" element={<LifecycleHiring />} />
                <Route path="movements" element={<LifecycleMovements />} />
                <Route path="exits" element={<LifecycleExits />} />
                <Route path="org" element={<LifecycleOrg />} />
                <Route path="payroll" element={<LifecyclePayroll />} />
              </Route>
              <Route path="leave" element={<Leave />}>
                <Route index element={<LeaveIndex />} />
                <Route path="accrual" element={<LeaveAccrual />} />
                <Route path="compoff" element={<LeaveCompOff />} />
              </Route>
              <Route path="expenses" element={<Expenses />}>
                <Route index element={<ExpensesIndex />} />
                <Route path="advances" element={<ExpensesAdvances />} />
              <Route path="travel" element={<ExpensesTravel />} />
              </Route>
              <Route path="payroll" element={<Payroll />}>
                <Route index element={<PayrollIndex />} />
                <Route path="gratuity" element={<PayrollGratuity />} />
                <Route path="adjustments" element={<PayrollAdjustments />} />
              </Route>
              <Route path="appraisals" element={<Appraisals />}>
                <Route index element={<AppraisalsIndex />} />
                <Route path="scoring" element={<AppraisalsScoring />} />
              </Route>
              <Route path="recruitment" element={<Recruitment />}>
                <Route index element={<RecruitmentIndex />} />
                <Route path="new" element={<RecruitmentNew />} />
                <Route path="request" element={<RecruitmentRequest />} />
                <Route path="channels" element={<RecruitmentChannels />} />
                <Route path="paperwork" element={<RecruitmentPaperwork />} />
                <Route path="correspondence" element={<RecruitmentCorrespondence />} />
                <Route path=":opening" element={<RecruitmentOpening />} />
                <Route path=":opening/:candidate" element={<RecruitmentCandidate />} />
              </Route>
              <Route path="workforce" element={<PeopleWorkforce />} />
              <Route path="assignments" element={<PeopleAssignments />} />
              <Route path="assignments/:person" element={<PeoplePerson />} />
              <Route path="conduct" element={<PeopleConduct />} />
              <Route path="files" element={<PeopleFiles />} />
              <Route path="compliance" element={<PeopleCompliance />} />
              <Route path="learning" element={<Learning />}>
                <Route index element={<LearningIndex />} />
                <Route path="programmes" element={<LearningProgrammes />} />
                <Route path="needs" element={<LearningNeeds />} />
                <Route path="outcomes" element={<LearningOutcomes />} />
                <Route path="matrix" element={<LearningMatrix />} />
                <Route path="succession" element={<LearningSuccession />} />
              </Route>
            </Route>
            {/* master data is created here and nowhere else; the add-ons hand over to these (D-142) */}
            <Route path="/items/new" element={<MasterForm kind="item" />} />
            <Route path="/assets/new" element={<MasterForm kind="asset" />} />
            <Route path="/customers/new" element={<MasterForm kind="customer" />} />
            <Route path="/suppliers/new" element={<NewSupplierFile />} />
            <Route path="/people/directory/new" element={<EmployeeForm />} />
            <Route path="/people/directory/:employee/edit" element={<EmployeeForm />} />
            <Route path="/contractors" element={<Contractors />} />
            <Route path="/contractors/new" element={<ContractorForm />} />
            <Route path="/contractors/:name" element={<ContractorDetail />} />
            <Route path="/contractors/:name/edit" element={<ContractorForm />} />
            <Route path="/demo-metrics" element={<DemoMetrics />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/process" element={<Process />} />
            <Route path="/pos" element={<Pos />} />
            <Route path="/procure" element={<Procure />} />
            <Route path="/procure/request/:name" element={<Procure view="request" />} />
            <Route path="/procure/request/:name/letter/:supplier" element={<RfqLetter />} />
            <Route path="/procure/onboarding/:name" element={<Procure view="onboarding" />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/statement/:name" element={<Orders view="statement" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </ErrorBoundary>
  );
}
